import { useEffect, useRef } from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  handleProductImageError,
  resolveProductImageSrc,
} from "../../lib/fallback-image";
import type { GalleryImage } from "../../types";

interface Props {
  images: GalleryImage[];
  fallbackImage: string;
  altName: string;
}

const _GALLERY_ARIA = "Product image gallery";

function thumbSrc(img: GalleryImage, fallback: string): string {
  return resolveProductImageSrc(img.medium ?? img.large ?? img.small ?? fallback);
}

/* 제품상세 Feature §4-1 — PDP 상단 갤러리.
 * 좌우 스와이프 + 첫/마지막 경계에서 더 이상 스와이프 불가(loop=false) + 중앙 이미지 확대(centeredSlides).
 * 확대 팝업(라이트박스)은 기획에서 제외됨(2026-06). */
export function FeatureGallery({ images, fallbackImage, altName }: Props) {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const rafRef = useRef<number>(0);
  const slides =
    images.length > 0
      ? images
      : [{ medium: fallbackImage, large: fallbackImage, small: fallbackImage }];
  const multiple = slides.length > 1;
  const slideCount = slides.length;
  const isLoop = false;
  let swiperClassName = 'ai-gp1-dp--list swiper pick-feature__swiper';

  const scheduleUpdate = () => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      const swiper = swiperRef.current;
      if (!swiper || swiper.destroyed) return;
      swiper.update();
    });
  };

  useEffect(() => {
    const list =
      images.length > 0
        ? images
        : [{ medium: fallbackImage, large: fallbackImage, small: fallbackImage }];
    for (const img of list) {
      const el = new Image();
      el.src = thumbSrc(img, fallbackImage);
    }
  }, [images, fallbackImage]);

  useEffect(() => {
    scheduleUpdate();
    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [slideCount, multiple, isLoop]);

  return (
    <div className="pick-feature__gallery-swiper" aria-label={_GALLERY_ARIA}>
      <Swiper
        className={swiperClassName}
        wrapperClass="swiper-wrapper"
        modules={[Pagination]}
        pagination={multiple ? { clickable: true } : false}
        centeredSlides={multiple ? true : false}
        slidesPerView="auto"
        spaceBetween={multiple ? 16 : 0}
        loop={isLoop}
        slideToClickedSlide={true}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          scheduleUpdate();
        }}
        watchOverflow={true}
        updateOnWindowResize={true}
      >
        {slides.map((img, idx) => (
          <SwiperSlide key={idx}>
            <div className="pick-feature__gallery-slide">
              <div
                className="pick-feature__gallery-main"
                aria-label={`${altName} ${idx + 1}/${slides.length}`}
              >
                <img
                  src={thumbSrc(img, fallbackImage)}
                  alt={`${altName} ${idx + 1}`}
                  loading={idx === 0 ? "eager" : "lazy"}
                  draggable={false}
                  onLoad={() => {
                    if (idx > 1) return;
                    scheduleUpdate();
                  }}
                  onError={handleProductImageError}
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
